import { useCallback, useState } from "react";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { DropDown } from "../../../../common/domain/models/dropdown";
import { ManutencaoLista } from "../../domain/models/manutencao_lista";
import { useManutencaoSolicitacaoFilters } from "../hooks/ManutencaoSolicitacao_lista/filter";
import { useManutencaoSolicitacaoPagination } from "../hooks/ManutencaoSolicitacao_lista/pagination";

type LoadManutencaoState = {
  nroManutencao?: number | null;
  idsMaquinaEquip?: number[] | null;
  idsCentroCusto?: number[] | null;
  idsFuncionarioSolicitante?: number[] | null;
  idsFuncionarioAprovacao?: number[] | null;
  idsFornecedorResponsavel?: number[] | null;
  dataSolicitacaoDe?: Date | null;
  dataSolicitacaoAte?: Date | null;
  status?: string[] | null;
  page?: number | null;
  rowsPerPage?: number | null;
};

const convertStatusStringToNumber = (status: string) => {
  switch (status) {
    case 'E':
      return 0;
    case 'A':
      return 1;
    case 'R':
      return 2;
    case 'F':
      return 3;
    default:
      return 0;
  }
}

const convertStatusNumberToString = (status: number) => {
  switch (status) {
    case 0:
      return 'E';
    case 1:
      return 'A';
    case 2:
      return 'R';
    case 3:
      return 'F';
    default:
      return 'E';
  }
}

export const useManutencaoSolicitacaoListaViewModel = () => {

  const {
    controlePatrimonio: {
      dropDownMaquinaEquipUseCase,
      getAllManutencaoUseCase,
      deleteManutencaoUseCase,
    },

    commons: {
      getFuncionariosDropDownUseCase,
      getFornecedorDropDownUseCase,
      getCentroCustoDropDownUseCase,

    },

  } = useDependencyInjection();

  const { pushErrorNotification } = usePageLoader();

  const filters = useManutencaoSolicitacaoFilters();
  const pagination = useManutencaoSolicitacaoPagination();
  const debouncedLoad = useCreateDebounceHandler(750);

  const [listaManutencao, setListaManutencao] = useState<ManutencaoLista[]>();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);


  const [openDelecaoModal, setOpenDelecaoModal] = useState<boolean>(false);
  const [selectedIdManutencao, setSelectedIdManutencao] = useState<number>();


  const [ddlFornecedor, setDdlFornecedor] = useState<DropDown[]>([]);
  const [ddlMaquinaEquip, setDdlMaquinaEquip] = useState<DropDown[]>([]);
  const [ddlFuncionario, setDdlFuncionario] = useState<DropDown[]>([]);
  const [ddlCentroCusto, setDdlCentroCusto] = useState<DropDown[]>([]);
  const [ddlStatus, setDdlStatus] = useState<DropDown[]>([]);

  const backOrNavigate = useBackOrNavigate();

  const statusOptions = [
    { title: "Em Aberto", id: 0 },
    { title: "Andamento", id: 1 },
    { title: "Reprovada", id: 2 },
    { title: "Finalizada", id: 3 },
  ];

  //#region  CARREGAMENTO
  const loadAllManutencaoLista = useCallback(
    async (funcState?: LoadManutencaoState) => {
      try {
        setLoading(true);

        const filteredNroManutencao = evaluateParamValue(funcState?.nroManutencao, filters.nroManutencao);
        const filteredIdsMaquinaEquip = evaluateParamValue(funcState?.idsMaquinaEquip, filters.idsMaquinaEquip);
        const filteredIdsCentroCusto = evaluateParamValue(funcState?.idsCentroCusto, filters.idsCentroCusto);
        const filteredIdsSolicitante = evaluateParamValue(funcState?.idsFuncionarioSolicitante, filters.idsFuncionarioSolicitante);
        const filteredIdsAprovacao = evaluateParamValue(funcState?.idsFuncionarioAprovacao, filters.idsFuncionarioAprovacao);
        const filteredIdsFornecedor = evaluateParamValue(funcState?.idsFornecedorResponsavel, filters.idsFornecedorResponsavel);
        const filteredDataDe = evaluateParamValue(funcState?.dataSolicitacaoDe, filters.dataSolicitacaoDe);
        const filteredDataAte = evaluateParamValue(funcState?.dataSolicitacaoAte, filters.dataSolicitacaoAte);
        const filteredPage = funcState?.page ?? pagination.page;
        const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;

        const normalizedFilterStatus = filters?.status ? filters.status.map(s => convertStatusNumberToString(s)) : null;
        const filteredStatus = evaluateParamValue(funcState?.status, normalizedFilterStatus);


        await getAllManutencaoUseCase.execute(
          filteredNroManutencao ?? undefined,
          filteredIdsMaquinaEquip ?? [],
          filteredIdsCentroCusto ?? [],
          filteredIdsSolicitante ?? [],
          filteredIdsAprovacao ?? [],
          filteredIdsFornecedor ?? [],
          filteredDataDe ?? undefined,
          filteredDataAte ?? undefined,
          filteredStatus ?? [],
          filteredPage,
          filteredRowsPerPage,
        );

        setListaManutencao(getAllManutencaoUseCase.items);
        pagination.setRowsPerPage(getAllManutencaoUseCase.pageSize);
        pagination.setTotalItems(getAllManutencaoUseCase.totalItems);
        setLoading(false);
      } catch (e: unknown) {
        setListaManutencao([]);
        setLoading(false);
        pushErrorNotification("Error", processError(e, "Falha ao buscar lista de manutenção!"));
      }
    },
    [
      filters,
      pagination,
      getAllManutencaoUseCase,
      pushErrorNotification
    ],
  );

  const loadFornecedorDropDown = useCallback(async () => {
    try {
      const response = await getFornecedorDropDownUseCase.getFornecedorDropDown();
      setDdlFornecedor(response.map((f) => ({ id: f.idFornecedor, title: f.nomeFantasia })));
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao carregar Fornecedores!"));
    }
  }, [getFornecedorDropDownUseCase, pushErrorNotification]);


  const loadFuncionarioDropDown = useCallback(async () => {
    try {
      const response = await getFuncionariosDropDownUseCase.getFuncionariosDropDown();
      setDdlFuncionario(response.map((f) => ({ id: f.idFuncionario, title: f.nome })));
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao carregar Funcionários!"));
    }
  }, [getFuncionariosDropDownUseCase, pushErrorNotification]);

  const loadMaquinaEquipDropDown = useCallback(async () => {
    try {
      const response = await dropDownMaquinaEquipUseCase.dropDownMaquinaEquip();
      setDdlMaquinaEquip(response.map((m) => ({ id: m.idMaquinaEquip, title: m.descricao })));
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao carregar Máquinas/Equipamentos!"));
    }
  }, [dropDownMaquinaEquipUseCase, pushErrorNotification]);

  const loadCentroCustoDropDown = useCallback(async () => {
    try {
      const response = await getCentroCustoDropDownUseCase.getCentroCustoDropDown();
      setDdlCentroCusto(response.map((c) => ({ id: c.id, title: c.title })));
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao carregar Centros de Custo!"));
    }
  }, [getCentroCustoDropDownUseCase, pushErrorNotification]);



  //#endregion


  //#region PAGE START
  usePageStartBootstrap(async () => {
    try {
      await loadFuncionarioDropDown();
      await loadFornecedorDropDown();
      await loadMaquinaEquipDropDown();
      await loadCentroCustoDropDown();
      await loadAllManutencaoLista();
      setDdlStatus(statusOptions);
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar dados!"));
    }
  });

  //#endregion


  //#region  PAGINAÇÃO
  const handlePageChange = useCallback(
    async (page: number) => {
      try {
        setLoading(true);
        await loadAllManutencaoLista({ page: page + 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao paginar!"));
      } finally {
        setLoading(false);
      }
    },
    [loadAllManutencaoLista, pushErrorNotification],
  );

  const handleRowsPerPageChange = useCallback(
    async (rowsPerPage: number) => {
      try {
        setLoading(true);
        await loadAllManutencaoLista({ page: 1, rowsPerPage: rowsPerPage });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao paginar!"));
      } finally {
        setLoading(false);
      }
    },
    [loadAllManutencaoLista, pushErrorNotification],
  );

  //#endregion



  //#region  PESQUISA

  const handleFornecedorChange = useDebouncedCallback(
    debouncedLoad,
    (newValue: number[]) => {
      filters.setIdsFornecedorResponsavel(newValue);
      return async () => await loadAllManutencaoLista({ idsFornecedorResponsavel: newValue });
    },
    [filters, loadAllManutencaoLista],
  );

  const handleFuncionarioAprovacaoChange = useDebouncedCallback(
    debouncedLoad,
    (newValue: number[]) => {
      filters.setIdsFuncionarioAprovacao(newValue);
      return async () => await loadAllManutencaoLista({ idsFuncionarioAprovacao: newValue });
    },
    [filters, loadAllManutencaoLista],
  );

  const handleFuncionarioSolicitanteChange = useDebouncedCallback(
    debouncedLoad,
    (newValue: number[]) => {
      filters.setIdsFuncionarioSolicitante(newValue);
      return async () => await loadAllManutencaoLista({ idsFuncionarioSolicitante: newValue });
    },
    [filters, loadAllManutencaoLista],
  );

  const handleMaquinaEquipChange = useDebouncedCallback(
    debouncedLoad,
    (newValue: number[]) => {
      filters.setIdsMaquinaEquip(newValue);
      return async () => await loadAllManutencaoLista({ idsMaquinaEquip: newValue });
    },
    [filters, loadAllManutencaoLista],
  );

  const handleStatusChange = useDebouncedCallback(
    debouncedLoad,
    (newValue: number[]) => {
      const normalizedStatus: string[] = newValue.map(v => convertStatusNumberToString(v));
      filters.setStatus(newValue);
      return async () => await loadAllManutencaoLista({ status: normalizedStatus });
    },
    [filters, loadAllManutencaoLista],
  );

  const handleIdCentroCustoChange = useDebouncedCallback(
    debouncedLoad,
    (newValue: number[]) => {
      filters.setIdsCentroCusto(newValue);
      return async () => await loadAllManutencaoLista({ idsCentroCusto: newValue });
    },
    [filters, loadAllManutencaoLista],
  );

  const handleNroManutencaoChange = useDebouncedCallback(
    debouncedLoad,
    (newValue: number | null) => {
      filters.setNroManutencao(newValue);
      return async () => await loadAllManutencaoLista({ nroManutencao: newValue });
    },
    [filters, loadAllManutencaoLista],
  );

  const handleDataChange = useDebouncedCallback(
    debouncedLoad,
    (de: Date | null, ate: Date | null) => {
      filters.setDataSolicitacaoDe(de);
      filters.setDataSolicitacaoAte(ate);
      return async () => await loadAllManutencaoLista({ dataSolicitacaoDe: de, dataSolicitacaoAte: ate });
    },
    [filters, loadAllManutencaoLista],
  );

  const handleClear = useCallback(async () => {
    filters.clearFilters();
    await loadAllManutencaoLista({
      nroManutencao: null,
      idsFornecedorResponsavel: [],
      idsFuncionarioAprovacao: [],
      idsFuncionarioSolicitante: [],
      idsMaquinaEquip: [],
      idsCentroCusto: [],
      status: null,
      dataSolicitacaoDe: null,
      dataSolicitacaoAte: null,

      page: 1,
      rowsPerPage: pagination.rowsPerPage,
    });
  }, [filters, pagination.rowsPerPage, loadAllManutencaoLista]);

  //#endregion


  //#region DELEÇÃO

  const handleDeleteOpenState = useCallback((idManutencao: number | null, openState: boolean) => {
    setOpenDelecaoModal(openState);
    if (idManutencao) {
      setSelectedIdManutencao(idManutencao);
    } else {
      setSelectedIdManutencao(undefined);
    }
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!selectedIdManutencao) return;
    try {
      setSaving(true);
      await deleteManutencaoUseCase.execute(selectedIdManutencao);
      await loadAllManutencaoLista();
      setOpenDelecaoModal(false);
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao excluir manutenção!"));
    } finally {
      setSaving(false);
    }
  }, [
    selectedIdManutencao,
    deleteManutencaoUseCase,
    loadAllManutencaoLista,
    pushErrorNotification
  ]);

  //#endregion


  //#region CRIAÇÃO E EDIÇÃO
  const handleVisualizar = useCallback(
    (id: number | null, openState: boolean) => {
      if (!openState) window.open(`/ManutencaoSolicitacao_cadastro?id=${id}&visualizar=true`, "_blank", "noopener,noreferrer");
      else backOrNavigate(`/ManutencaoSolicitacao_cadastro?id=${id}&visualizar=true`);
    },
    [backOrNavigate],
  );

  const handleCreate = useCallback(() => backOrNavigate(`/ManutencaoSolicitacao_cadastro?visualizar=false`), [backOrNavigate]);

  //#endregion

  return {
    listaManutencao,
    filters,
    pagination,
    loading,
    saving,
    ddlStatus: ddlStatus ?? [],
    ddlFornecedor: ddlFornecedor ?? [],
    ddlFuncionario: ddlFuncionario ?? [],
    ddlMaquinaEquip: ddlMaquinaEquip ?? [],
    ddlCentroCusto: ddlCentroCusto ?? [],
    openDelecaoModal,

    handleStatusChange,
    handleDeleteOpenState,
    handleDeleteConfirm,
    loadAllManutencaoLista,
    handlePageChange,
    handleRowsPerPageChange,
    handleClear,
    handleFornecedorChange,
    handleFuncionarioAprovacaoChange,
    handleFuncionarioSolicitanteChange,
    handleNroManutencaoChange,
    handleIdCentroCustoChange,
    handleMaquinaEquipChange,
    handleDataChange,
    handleVisualizar,
    handleCreate,
  };
};
